import {
  ASSESSMENT_PENDING_STATUSES,
  ASSESSMENT_PROCESSING_STATUSES,
  ASSESSMENT_REVIEW_PROCESSING_STATUSES,
  ASSESSMENT_REVIEW_STATUSES,
  RISK_ASSESSMENT_STATUS,
  ASSESSMENT_TYPE_PARAM,
} from "@root/constants";
import { ASSESSMENT_STATUS } from "@root/components/assessments/actionConstants";

interface Assessment {
  id: string;
  department: string;
  assessmentName: string;
  equipment: string;
  material: string;
  workDescription: string;
  date: string;
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc";
}

/**
 * @param apiData - Raw data from API
 * @returns Transformed assessment data
 */
export const transformAssessmentData = (apiData: any[]): Assessment[] => {
  if (!apiData || !Array.isArray(apiData)) return [];

  return apiData.map((item: any) => {
    const isUploaded = item?.assessmentType === ASSESSMENT_TYPE_PARAM.UPLOADED;
    const status = isUploaded
      ? getStatusFromUploadedAssessmentStatus(item?.status)
      : getStatusFromCreatedAssessmentStatus(item?.status);

    return {
      id: item?.id?.toString() || "",
      department: item?.department?.title || "",
      assessmentName: item?.name || "",
      equipment:
        item?.equipments?.map((eq: any) => eq?.title)?.join(", ") || "",
      material:
        item?.materials?.map((mat: any) => mat?.title)?.join(", ") || "",
      workDescription: item?.description || "",
      date: item?.createdAt
        ? new Date(item.createdAt).toISOString().split("T")[0]
        : "",
      mimeType: item?.mimeType || "",
      status,
    };
  });
};

/**
 * @param apiStatus - Status number from API for CREATED assessments (RISK_ASSESSMENT_STATUS)
 * @returns Assessment status string
 */
export const getStatusFromCreatedAssessmentStatus = (
  apiStatus: number
):
  | "pending"
  | "completed"
  | "in_progress"
  | "processing_sources"
  | "failed" => {
  if (apiStatus === RISK_ASSESSMENT_STATUS.COMPLETED)
    return ASSESSMENT_STATUS.COMPLETED;
  if (apiStatus === RISK_ASSESSMENT_STATUS.EXCEPTION)
    return ASSESSMENT_STATUS.FAILED;
  // Handle processing sources statuses (STREAMING_COMPLETED and beyond)
  if (ASSESSMENT_PROCESSING_STATUSES.includes(apiStatus))
    return ASSESSMENT_STATUS.PROCESSING_SOURCES;
  // Handle in-progress statuses (START, RISK_VS_DOCS, STREAMING)
  if (ASSESSMENT_PENDING_STATUSES.includes(apiStatus))
    return ASSESSMENT_STATUS.IN_PROGRESS;
  return ASSESSMENT_STATUS.PENDING;
};

/**
 * @param apiStatus - Status number from API for UPLOADED assessments (ASSESSMENT_REVIEW_STATUSES)
 * @returns Assessment status string
 */
export const getStatusFromUploadedAssessmentStatus = (
  apiStatus: number
):
  | "pending"
  | "completed"
  | "in_progress"
  | "processing_sources"
  | "failed"
  | "irrelevant_doc" => {
  if (apiStatus === ASSESSMENT_REVIEW_STATUSES.COMPLETED)
    return ASSESSMENT_STATUS.COMPLETED;
  if (apiStatus === ASSESSMENT_REVIEW_STATUSES.IRRELEVANT_DOC)
    return ASSESSMENT_STATUS.IRRELEVANT_DOC;
  if (apiStatus === ASSESSMENT_REVIEW_STATUSES.EXCEPTION)
    return ASSESSMENT_STATUS.FAILED;

  // STREAMING status should be clickable - map to processing_sources
  if (apiStatus === ASSESSMENT_REVIEW_STATUSES.STREAMING)
    return ASSESSMENT_STATUS.PROCESSING_SOURCES;

  // Other processing statuses for uploaded assessments map to in_progress
  // (START, DOWNLOADED_PDF_FILE, READ_FILE_CONTENT, ACCIDENT_VS_DOCS, etc.)
  if (ASSESSMENT_REVIEW_PROCESSING_STATUSES.includes(apiStatus))
    return ASSESSMENT_STATUS.IN_PROGRESS;
  return ASSESSMENT_STATUS.PENDING;
};

/**
 * @deprecated Use getStatusFromCreatedAssessmentStatus or getStatusFromUploadedAssessmentStatus instead
 * @param apiStatus - Status number from API
 * @returns Assessment status string
 * This function is kept for backward compatibility but should not be used for new code.
 * It defaults to created assessment status mapping.
 */
export const getStatusFromApiStatus = (
  apiStatus: number
):
  | "pending"
  | "completed"
  | "in_progress"
  | "processing_sources"
  | "failed" => {
  // Default to created assessment status mapping for backward compatibility
  return getStatusFromCreatedAssessmentStatus(apiStatus);
};

/**
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatAssessmentDate = (dateString: string): string => {
  if (!dateString) return "";
  return new Date(dateString).toISOString().split("T")[0];
};

/**
 * @param status - Assessment status
 * @returns Color code for the status
 */
export const getStatusColor = (
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc"
): string => {
  const statusColors = {
    [ASSESSMENT_STATUS.PENDING]: "#FFA500",
    [ASSESSMENT_STATUS.COMPLETED]: "#3AB65A",
    [ASSESSMENT_STATUS.IN_PROGRESS]: "#005EB8",
    [ASSESSMENT_STATUS.PROCESSING_SOURCES]: "#06B6D4",
    [ASSESSMENT_STATUS.FAILED]: "#DC3545",
    [ASSESSMENT_STATUS.IRRELEVANT_DOC]: "#E77E07",
  };
  return statusColors[status] || "#5C6670";
};

/**
 * @param status - Assessment status
 * @returns Background color code for the status
 */
export const getStatusBgColor = (
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc"
): string => {
  const statusBgColors = {
    [ASSESSMENT_STATUS.PENDING]: "#FFA50026",
    [ASSESSMENT_STATUS.COMPLETED]: "#3AB65A26",
    [ASSESSMENT_STATUS.IN_PROGRESS]: "#005EB826",
    [ASSESSMENT_STATUS.PROCESSING_SOURCES]: "#06B6D426",
    [ASSESSMENT_STATUS.FAILED]: "#DC354526",
    [ASSESSMENT_STATUS.IRRELEVANT_DOC]: "#E77E0726",
  };
  return statusBgColors[status] || "#5C667026";
};
